/**
 * hsnBackfill.ts — retroactively fill missing HSN/SAC on already-booked invoice
 * line items from the current product master.
 *
 * Invoice lines snapshot the product's HSN at sale time, so fixing the product
 * catalog does NOT heal historical invoices. This backfills them. HSN is
 * descriptive metadata (it does not change tax amounts), so this is a safe,
 * non-financial correction — but it is still audit-logged.
 *
 * The candidate-selection logic is a pure function so it can be unit-tested
 * without a database.
 */

import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import { Invoice, LineItem } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import { logActivity } from '@/utils/auditLog';

const HSN_REGEX = /^\d{4}$|^\d{6}$|^\d{8}$/;
const CHUNK = 500;

export interface BackfillLineLike {
  id: string;
  productId: string;
  hsnCode: string | null;
  description: string | null;
}

export interface BackfillProductLike {
  hsnCode: string | null;
  name: string;
}

export interface HsnBackfillCandidate {
  lineId: string;
  hsn: string;
  /** Product name — used to also fill a blank line description. */
  name: string;
  descriptionWasBlank: boolean;
}

/** True when a line has no usable HSN of its own. */
function lineMissingHsn(hsn: string | null): boolean {
  return !hsn || !HSN_REGEX.test(hsn.trim());
}

/**
 * Pure: pick the line items that can be backfilled — those missing a valid HSN
 * whose product now carries a valid one.
 */
export function selectHsnBackfill(
  lines: BackfillLineLike[],
  productById: Map<string, BackfillProductLike>,
): HsnBackfillCandidate[] {
  const out: HsnBackfillCandidate[] = [];
  for (const li of lines) {
    if (!lineMissingHsn(li.hsnCode)) continue;
    const p = productById.get(li.productId);
    const phsn = p?.hsnCode?.trim();
    if (!p || !phsn || !HSN_REGEX.test(phsn)) continue;
    out.push({
      lineId: li.id,
      hsn: phsn,
      name: p.name,
      descriptionWasBlank: !li.description || li.description.trim() === '',
    });
  }
  return out;
}

async function fetchInChunks<T>(ids: string[], run: (batch: string[]) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    out.push(...(await run(ids.slice(i, i + CHUNK))));
  }
  return out;
}

/** Gather backfillable line items (+ the resolved HSN) for a store. */
async function gather(storeId: string): Promise<{ lines: LineItem[]; candidates: HsnBackfillCandidate[] }> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
  ).fetch();
  const invIds = invoices.map((i) => i.id);

  const lines = await fetchInChunks(invIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const missing = lines.filter((l) => lineMissingHsn(l.hsnCode));

  const prodIds = [...new Set(missing.map((l) => l.productId).filter(Boolean))];
  const products = await fetchInChunks(prodIds, (batch) =>
    database.get<Product>('products').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const productById = new Map<string, BackfillProductLike>();
  for (const p of products) productById.set(p.id, { hsnCode: p.hsnCode, name: p.name });

  const candidates = selectHsnBackfill(
    missing.map((l) => ({ id: l.id, productId: l.productId, hsnCode: l.hsnCode, description: l.description })),
    productById,
  );
  return { lines: missing, candidates };
}

/** How many historical line items can be backfilled right now. */
export async function countHsnBackfill(storeId: string): Promise<number> {
  const { candidates } = await gather(storeId);
  return candidates.length;
}

/** Apply the backfill; returns the number of line items updated. */
export async function applyHsnBackfill(storeId: string): Promise<number> {
  const { lines, candidates } = await gather(storeId);
  if (candidates.length === 0) return 0;

  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const affectedInvoices = new Set<string>();

  await txn(async (db) => {
    const ops = candidates.map((c) => {
      const li = byId.get(c.lineId)!;
      affectedInvoices.add(li.invoiceId);
      return li.prepareUpdate((r: LineItem) => {
        r.hsnCode = c.hsn;
        if (c.descriptionWasBlank) r.description = c.name;
        r.isSynced = false;
        r.version += 1;
      });
    });
    await db.batch(...ops);
  });

  void logActivity({
    action: 'invoice_hsn_backfilled',
    entityType: 'invoice',
    entityId: storeId,
    storeId,
    changes: {
      summary: `Backfilled HSN on ${candidates.length} line item(s) across ${affectedInvoices.size} invoice(s) from the product catalog`,
      quantity: candidates.length,
    },
  });

  return candidates.length;
}
